import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Gem, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { db } from "@/lib/billing";
import type { TemplateRecord } from "@/lib/packageAccess";
import TemplateRenderer from "@/templates/TemplateRenderer";

async function fetchTemplate(id: string): Promise<TemplateRecord | null> {
  const { data, error } = await db.from("site_templates").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

export default function TemplatePreviewPage() {
  const { id } = useParams();
  const { data: template, isLoading } = useQuery({ queryKey: ["site-template-preview", id], queryFn: () => fetchTemplate(id!), enabled: !!id });
  if (isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!template) return <Navigate to="/templates" replace />;

  const editionLabel =
    template.exclusivity_edition === "signature" ? "Signature"
    : template.exclusivity_edition === "numbered" ? "Edition numerotee"
    : "Design unique";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold">Preview &mdash; {template.name}</h1>
            <p className="text-xs text-muted-foreground">{template.target_niche} &middot; {template.language} &middot; {template.layout_key ?? "broadsheet"}</p>
          </div>
          {template.is_exclusive && (
            <Badge variant="outline" className="border-accent-press text-accent-press">
              <Gem className="mr-1 h-3 w-3" />
              {editionLabel}
            </Badge>
          )}
        </div>
        <Button asChild variant="outline" size="sm"><Link to={`/templates/${template.id}`}><ArrowLeft className="mr-2 h-4 w-4" />Details</Link></Button>
      </header>
      <main>
        {template.layout_key ? (
          <div className="border-b border-border">
            <TemplateRenderer template={template as TemplateRecord & { layout_key: string }} />
          </div>
        ) : template.demo_url ? (
          <div className="p-6">
            <iframe title={template.name} src={template.demo_url} className="h-[78vh] w-full rounded-md border-0" />
            <Button asChild className="mt-4" variant="outline"><a href={template.demo_url} target="_blank" rel="noreferrer">Ouvrir la demo <ExternalLink className="ml-2 h-4 w-4" /></a></Button>
          </div>
        ) : template.preview_image_url ? (
          <div className="p-6">
            <img src={template.preview_image_url} alt={template.name} className="max-h-[78vh] w-full rounded-md object-contain bg-muted" />
          </div>
        ) : (
          <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">Aucun apercu disponible.</div>
        )}
      </main>
    </div>
  );
}
